import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {SVG} from '../../../../../constants/assetLists';
import AppDialog from '../../../../../components/common/AppDialog';
import {useAnnouncementTaskMutations} from '../../../../../hooks/useAnnouncementTaskMutations';
import moment from 'moment';
import FileBox from '../../AnnouncementsAndTasks/components/FileBox';
import FormBox from '../../AnnouncementsAndTasks/components/FormBox';
import TaskCard from '../../AnnouncementsAndTasks/components/TaskCard';
import ChecklistBox from '../../AnnouncementsAndTasks/components/ChecklistBox';
import AppButton from '../../../../../components/common/AppButton';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import QUERY_KEYS from '../../../../../constants/queryKeys';
import {
  handleDeleteAnnouncementService,
  handleGetAnnouncementService,
} from '../../../../../services/operations/announcements';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import {ICONS} from '../../../../users/data';
import {
  getRoleTypeIcon,
  getRoleTypeName,
  isReservationAnnouncement,
  isSuperAdmin,
} from '../../../../../utils';
import ReservationAnnouncementLabel from '../../AnnouncementsAndTasks/components/ReservationAnnouncementLabel';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function AnnouncementView({open, onClose}: Props) {
  const queryClient = useQueryClient();

  const [showRespondersDropdown, setShowRespondersDropdown] = useState(false);
  const [showCancelConfirmationDialog, setShowCancelConfirmationDialog] =
    useState(false);

  const respondersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    setOpenAddEditAnnouncementDialog,
    setSelectedAnnouncementForEdit,
    setAnnouncementModalMode,
    selectedAnnouncementId,
  } = useOperationsContext();
  const {
    toggleAnnouncementResponderMutation,
    updateAnnouncementMutation,
    readAnnouncementMutation,
  } = useAnnouncementTaskMutations();

  const {
    data: announcement,
    refetch,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_ANNOUNCEMENT, selectedAnnouncementId],
    queryFn: () => handleGetAnnouncementService(selectedAnnouncementId!),
    enabled: open && !!selectedAnnouncementId,
    select: data => data?.data,
    refetchInterval: open && selectedAnnouncementId ? 60000 : false,
  });
  const {showSnackbar} = useSnackbar();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<{
    top: number;
    right: number;
  } | null>(null);
  useEffect(() => {
    if (!open || !selectedAnnouncementId) return;
    refetch();
  }, [open, selectedAnnouncementId, refetch]);

  const storedUser = localStorage.getItem('user');
  const currentUserId = useMemo(() => {
    return storedUser ? JSON.parse(storedUser)?._id : null;
  }, [storedUser]);

  const hasResponded = useMemo(() => {
    if (!currentUserId || !announcement?.respondedUsers) return false;
    return announcement.respondedUsers.some(
      (r: any) => r?.user?._id === currentUserId,
    );
  }, [announcement?.respondedUsers, currentUserId]);

  const createdByMe = announcement?.createdBy?._id === currentUserId;
  const hasSameRole =
    announcement?.createdBy?.roles?.[0] ===
    JSON.parse(storedUser!)?.roles?.[0]?._id;

  const showAnnouncementDoneCTA = useMemo(() => {
    if (!announcement || announcement.announcementType === 'Notice')
      return false;
    const superAdmin = isSuperAdmin();
    const isAssignedToMe = announcement?.assignee?.some(
      (a: {_id: string}) => a._id === currentUserId,
    );
    if (createdByMe || isAssignedToMe || superAdmin) return true;
    if (announcement?.enabledDeletion && hasSameRole) return true;
    return false;
  }, [announcement, currentUserId]);

  const handleUpdateAnnouncementStatus = () => {
    updateAnnouncementMutation.mutate({
      id: announcement._id,
      payload: {
        isCompleted: !announcement.isCompleted,
      },
    });
  };

  const handleToggleResponder = () => {
    toggleAnnouncementResponderMutation.mutate(announcement._id);
  };

  const handleRespondersMouseEnter = () => {
    if (respondersTimeoutRef.current)
      clearTimeout(respondersTimeoutRef.current);

    // compute position for portal-based dropdown
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      setDropdownStyle({
        top: Math.round(rect.bottom + 6),
        right: Math.round(window.innerWidth - rect.right),
      });
    } else {
      setDropdownStyle(null);
    }

    setShowRespondersDropdown(true);
  };

  const handleRespondersMouseLeave = () => {
    respondersTimeoutRef.current = setTimeout(() => {
      setShowRespondersDropdown(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (respondersTimeoutRef.current)
        clearTimeout(respondersTimeoutRef.current);
    };
  }, []);

  const isUrgent = announcement?.isUrgent;
  const isRegulation = announcement?.announcementType === 'Regulation';
  const annIcon = useMemo(() => {
    if (!announcement) return null;

    switch (announcement?.announcementType) {
      case 'Error':
        return SVG.redTriangleIcon;
      case 'Quick':
        return SVG.quickTypeIcon;
      case 'Reservation':
        return SVG.tableIcon;
      case 'Menu':
        return SVG.menuTypeIcon;
      case 'Training':
        return isUrgent ? SVG.tutorialsActiveRedIcon : SVG.tutorialsActiveIcon;
      case 'Administration':
        return isUrgent ? SVG.settingRedIcon : SVG.settingBlackIcon;
      case 'Regulation':
        return isUrgent
          ? SVG.administrationRedIcon
          : SVG.administrationSmallIcon;
      case 'Notice':
        return SVG.exclamationCircleIcon;
      default:
        return isUrgent
          ? SVG.regularAnnouncementRedIcon
          : SVG.regularAnnouncementIcon;
    }
  }, [announcement, isUrgent]);

  const isAllTasksDone =
    announcement?.tasks?.length > 0 &&
    announcement?.tasks?.every((t: any) => t.isCompleted);
  const isAlert = announcement?.announcementType == 'Error';
  const isRead = announcement?.read || isAllTasksDone;

  const startDateUtc = announcement?.scheduling?.startDate; // ISO UTC string
  const msPerDay = 24 * 60 * 60 * 1000;
  // Normalize both dates to UTC midnight to avoid timezone drift
  const utcStartOfDay = (d: Date) =>
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  const dueDate = startDateUtc ? new Date(startDateUtc) : null;
  const now = new Date();

  const daysLeft =
    dueDate == null
      ? null
      : Math.ceil((utcStartOfDay(dueDate) - utcStartOfDay(now)) / msPerDay);

  // YELLOW → 4 weeks to 1 week before startDate (28..8 days left)
  const isRegulationDue =
    announcement?.announcementType === 'Regulation' &&
    daysLeft != null &&
    daysLeft <= 28 &&
    daysLeft > 7;

  // RED → 1 week before OR on/after due date (<= 7 days left, including overdue)
  const isRegulationOverDue =
    announcement?.announcementType === 'Regulation' &&
    daysLeft != null &&
    daysLeft <= 7;
  const hasTasks = announcement?.tasks && announcement?.tasks.length > 0;
  const isAnnouncementDone = hasTasks
    ? isAllTasksDone && announcement?.isCompleted
    : announcement?.isCompleted;

  const statusIcon = useMemo(() => {
    const isAnnouncementDone = announcement?.isCompleted;
    const isAllTasksDone = hasTasks
      ? announcement?.tasks?.every((task: any) => task?.isCompleted)
      : false;
    const isTaskDone = announcement?.tasks?.some(
      (task: any) => task.isCompleted,
    );

    if (isAllTasksDone && isAnnouncementDone)
      return SVG.completeCheckboxWhiteIcon;

    if (!hasTasks && isAnnouncementDone) return SVG.completeCheckboxWhiteIcon;

    // If any task done, still unchecked until all done
    if (isTaskDone || isAnnouncementDone)
      return SVG.partiallyCompleteCheckboxIcon;

    return SVG.regularCheckboxIcon;
  }, [announcement]);

  useEffect(() => {
    if (!open) return;

    if (isError) {
      showSnackbar({
        message: '⚠️ ההודעה לא נמצאה!',
        type: 'info',
      });
      onClose();
    }
  }, [isError, open, showSnackbar, onClose]);

  useEffect(() => {
    const isUnread = announcement && !announcement.readStatus?.isRead;

    if (open && currentUserId && isUnread) {
      readAnnouncementMutation.mutate({
        id: announcement?._id,
        payload: {
          userId: currentUserId,
        },
      });
    }
  }, [
    open,
    announcement?._id,
    announcement?.readStatus?.isRead,
    currentUserId,
  ]);

  const isReversed = isReservationAnnouncement(announcement);

  const {mutate: hardDeleteAnnouncement, isPending: isCancelling} = useMutation(
    {
      mutationFn: (id: string) => handleDeleteAnnouncementService(id),
      onSuccess: async data => {
        onClose();
        showSnackbar({
          message: data?.message ?? 'משימת ההודעה נמחקה בהצלחה',
          type: 'success',
        });

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS],
          }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_BRIEF_ANNOUNCEMENTS_BY_DATE],
          }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_BY_DATE],
          }),
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_ALL_ANNOUNCEMENTS_FOR_STATS],
          }),
        ]);
      },
    },
  );

  const handleOpenCancelConfirmationDialog = useCallback(() => {
    if (!announcement?._id || isCancelling) return;
    setShowCancelConfirmationDialog(true);
  }, [announcement?._id, isCancelling]);

  const handleCloseCancelConfirmationDialog = useCallback(() => {
    setShowCancelConfirmationDialog(false);
  }, []);

  const handleConfirmCancelReservation = useCallback(() => {
    if (!announcement?._id) return;
    setShowCancelConfirmationDialog(false);
    hardDeleteAnnouncement(announcement._id);
  }, [announcement?._id, hardDeleteAnnouncement]);

  return (
    <AppDialog
      showModel={open}
      onClose={onClose}
      width={720}
      draggable
      footer={
        <div className="flex justify-end gap-2 border-t pt-3 border-t-gray70">
          {isReversed ? (
            <AppButton
              onClick={handleOpenCancelConfirmationDialog}
              disabled={isCancelling}
              isLoading={isCancelling}
              iconStart={
                <img
                  src={SVG.closeDarkIcon}
                  alt="close"
                  className="h-4 w-4 object-contain"
                />
              }>
              בוטל
            </AppButton>
          ) : (
            <>
              {showAnnouncementDoneCTA && (
                <AppButton
                  onClick={handleUpdateAnnouncementStatus}
                  variant={isAnnouncementDone ? 'primary' : undefined}
                  className={isAnnouncementDone ? '!bg-green' : undefined}>
                  <img src={statusIcon} alt="like" className="h-4 w-4" /> עשיתי
                </AppButton>
              )}

              {
                <AppButton onClick={handleToggleResponder}>
                  {' '}
                  <img
                    src={
                      hasResponded
                        ? SVG.thumbsUpGreenIcon
                        : SVG.thumbsUpBlueIcon
                    }
                    alt="like"
                    className="h-4 w-4"
                  />
                  על זה
                </AppButton>
              }
            </>
          )}
          <AppButton onClick={onClose}>סגירת חלון</AppButton>
        </div>
      }
      titleIcon={
        <img
          src={SVG.menuDarkIcon}
          alt="menu"
          className="w-[18px] h-[12px] object-contain"
        />
      }
      showHeaderLine={false}
      titleField={
        <>
          <div className="flex justify-end mb-2">
            <AppButton
              variant="custom"
              className="p-0"
              onClick={e => {
                e.stopPropagation();

                setSelectedAnnouncementForEdit(announcement);
                setAnnouncementModalMode('edit');
                setOpenAddEditAnnouncementDialog(true);
              }}>
              <img src={SVG.editPenCircle} alt="Edit" />
            </AppButton>
          </div>
          {isReversed ? (
            <ReservationAnnouncementLabel
              announcement={announcement}
              isAllTasksDone={isAllTasksDone}
              annIcon={annIcon || undefined}
            />
          ) : (
            <div
              className={`flex items-center justify-between  min-w-[333px] rounded-[8px] px-2 py-1 ${
                isRegulationDue
                  ? 'bg-brightYellow'
                  : isRegulationOverDue
                    ? 'bg-lightRed'
                    : ''
              }`}>
              <div className="flex gap-2 items-center">
                <img src={annIcon ?? ''} className="w-5 h-5" />

                <h2
                  className={`text-[22px]
        ${
          isRegulation && isUrgent
            ? 'text-[#810023]'
            : isAlert || isUrgent
              ? 'text-raspberryRed'
              : 'text-black'
        }
        ${isRead ? 'font-medium' : isUrgent ? 'font-bold' : 'font-semibold'}
        ${isAllTasksDone ? 'line-through' : ''}
      `}>
                  {announcement?.announcementTitle}
                </h2>
              </div>
            </div>
          )}
        </>
      }>
      {/* CONTENT */}
      <div className="flex flex-col gap-3 px-3">
        <h2>{announcement?.description}</h2>
        <div className="flex items-center space-x-2 text-sm text-grayText">
          <span>משימות: {announcement?.tasks?.length}</span>
          <span className="pr-2">|</span>
          <span>
            {announcement?.createdBy?.firstName || ''}{' '}
            {announcement?.createdBy?.lastName || ''}
          </span>
          <span>
            {announcement?.createdAt
              ? moment(announcement.createdAt).format('HH:mm DD/MM/YYYY')
              : ''}
          </span>
          {announcement?.enableRespondentCount && (
            <>
              <div className="w-[1px] h-[20px] bg-gray50" />
              <div
                ref={wrapperRef}
                className="inline-block"
                onMouseEnter={handleRespondersMouseEnter}
                onMouseLeave={handleRespondersMouseLeave}>
                <span className="text-linkBlue font-semibold cursor-pointer underline">
                  מס' משיבים: {announcement?.respondedCount ?? 0}
                </span>
              </div>

              {showRespondersDropdown &&
              announcement?.respondedUsers?.length > 0 &&
              dropdownStyle
                ? createPortal(
                    <div
                      onMouseEnter={handleRespondersMouseEnter}
                      onMouseLeave={handleRespondersMouseLeave}
                      className="min-w-[203px] max-h-[300px] overflow-y-auto"
                      style={{
                        position: 'fixed',
                        top: dropdownStyle.top,
                        right: dropdownStyle.right,
                        zIndex: 9999,
                        background: '#fff',
                        border: '1px solid #707070',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                        padding: '2px',
                      }}>
                      {announcement.respondedUsers.map((responder: any) => {
                        const user = responder?.user;
                        if (!user) return null;
                        const bgColor = user?.roles?.[0]?.color || '#E0E0E0';
                        const fullName =
                          `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

                        return (
                          <div
                            key={responder._id}
                            className="flex items-start gap-2 p-2 hover:bg-gray-50 rounded">
                            <div
                              className="w-[32px] h-[32px] rounded-full flex items-center justify-center shrink-0"
                              style={{backgroundColor: bgColor}}>
                              <span className="text-xl text-black">
                                {fullName.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <span>{fullName}</span>
                              {user.roles?.length > 0 &&
                                user?.roles?.[0]?.roleType && (
                                  <div className="flex flex-wrap gap-2 mt-1 mb-3">
                                    <span
                                      className="text-xs text-grayText px-2 py-0.5 rounded-full flex items-center gap-1"
                                      style={{backgroundColor: bgColor}}>
                                      <img
                                        src={
                                          ICONS.find(
                                            item =>
                                              item.id ===
                                              (getRoleTypeIcon(
                                                user?.roles?.[0]?.roleType,
                                              ) || user?.roles?.[0]?.icon),
                                          )?.icon
                                        }
                                        alt=""
                                        className="h-3 w-3"
                                      />
                                      <span>
                                        {getRoleTypeName(
                                          user?.roles?.[0]?.roleType,
                                        ) || ''}
                                      </span>
                                    </span>
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      })}
                    </div>,
                    document.body,
                  )
                : null}
            </>
          )}
        </div>

        {announcement?.checklists?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {announcement?.checklists.map((item: any, index: number) => (
              <ChecklistBox key={index} list={item} />
            ))}
          </div>
        )}

        {announcement?.files?.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {announcement?.files.map((file: any, index: number) => (
              <FileBox key={index} file={file} />
            ))}
          </div>
        )}

        {announcement?.forms && announcement.forms?._id && (
          <div className="flex flex-wrap gap-3">
            <FormBox form={announcement.forms} />
          </div>
        )}

        {announcement?.tasks?.length > 0 && (
          <div className="  flex flex-col gap-2">
            {announcement?.tasks.map((task: any, index: number) => {
              const normalizedTask = {
                ...task,
                id: task?._id,
                recipient:
                  task?.recipients && task?.recipients?.length > 0
                    ? {
                        assignedTo: `${task?.recipients[0]?.firstName ?? ''} ${task?.recipients[0]?.lastName ?? ''}`,
                      }
                    : null,
                taskStatus:
                  task?.taskStatus ??
                  (task?.isCompleted ? 'completed' : 'open'),
                description: task?.taskTitle ?? '',
                canAddComments: task?.canAddComments ?? true,
              };

              return (
                <TaskCard
                  key={index}
                  task={normalizedTask}
                  announcement={announcement}
                  index={index}
                />
              );
            })}
          </div>
        )}
      </div>

      <AppConfirmationDialog
        open={showCancelConfirmationDialog}
        onClose={handleCloseCancelConfirmationDialog}
        icon={<img src={SVG.deleteRedIcon} className="w-[48px] h-[48px]" />}
        title="שימו לב"
        subTitle="האם לבטל את ההזמנה?"
        confirmBtnText="כן"
        cancelBtnText="לא"
        onConfirm={handleConfirmCancelReservation}
      />
    </AppDialog>
  );
}
